import { useContext, useEffect, useState } from 'react';
import styles from './HastaPublicaEdit.module.scss';
import { useBackEndRequests } from '../lib/backEndRequests';
import { useQuery } from '@tanstack/react-query';
import { IDeclaracaoHastaPublica } from '../../../interfaces/IDeclaracoes';
import { useNavigate } from 'react-router-dom';
import { declaracaoHastaPublicaInicial, DiefCfemContext } from '../../../contexts/DiefCfemContext';
import { IIdentificacaoBasica } from '../../../interfaces/IIdentificacaoBasica';
import { ICrumb } from '../../../interfaces/ICrumb';
import { BrButton } from '@govbr-ds/react-components';
import { Button, Modal } from 'react-dsgov';
import CustomDivider from '../../../components/customDivider/customDivider';
import TituloSection from '../../../components/tituloSection/tituloSection';
import HastaPublicaIdBasica from '../components/hastaPublicaIdBasica/hastaPublicaIdBasica';
import HastaPublicaProcessoMinerario from '../components/hastaPublicaProcessoMinerario/hastaPublicaProcessoMinerario';
import HastaPublicaResumo from '../components/hastaPublicaResumo/hastaPublicaResumo';
import ErrorMessage from '../../../components/errorMessage/errorMessage';
import { useNotificacao } from '../../../hooks/useNotificacao';
import CustomConfirmer from '../../../components/customConfirmer/customConfirmer';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

function HastaPublicaEdit() {
  const urlParams = new URLSearchParams(window.location.search);
  const declaracaoId = urlParams.get('id') || '';
  const [mesDeclaracao, setMesDeclaracao] = useState<number | undefined>();
  const [anoDeclaracao, setAnoDeclaracao] = useState<number | undefined>();

  const [statusError, setStatusError] = useState<string | null>(null);

  const mostrarNotificacao = useNotificacao();

  const { getRascunhoDeclaracao, postEnviarDeclaracao, getIdentificacaoBasica, deleteDeclaracaoById } =
    useBackEndRequests();

  const {
    data: rascunhoData,
    isLoading: isRascunhoDataLoading,
    error: rascunhoError,
  } = useQuery<IDeclaracaoHastaPublica, IErrorWithSentryId>({
    queryKey: ['rascunhoDeclaracaoById', declaracaoId],
    queryFn: () => getRascunhoDeclaracao(declaracaoId),
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const cnpj = sessionStorage.getItem('userCpfCnpj');
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const context = useContext(DiefCfemContext);

  const [isNovaArrematacao, setNovaArrematacao] = useState(false);
  const [isRetificadora, setIsRetificadora] = useState(false);

  const [isModalEnviarDeclaracaoOpen, setIsModalEnviarDeclaracaoOpen] = useState(false);
  const [isModalExcluirRetificacaoOpen, setIsModalExcluirRetificacaoOpen] = useState(false);

  const {
    data: declaranteData,
    isLoading: isDeclaranteDataLoading,
    error: declaranteError,
  } = useQuery<IIdentificacaoBasica, IErrorWithSentryId>({
    queryKey: ['idBasicaQuery', cnpj],
    queryFn: () => getIdentificacaoBasica(cnpj || ''),
    refetchOnWindowFocus: false,
    retry: statusError === '404' || statusError === '400' || statusError === '403',
  });

  useEffect(() => {
    const crumbs: ICrumb[] = [
      {
        isHome: true,
        label: 'home',
        onClick: () => {
          navigate('/');
        },
      },
      {
        label: 'Arrematação em Hasta Pública',
        onClick: () => {
          navigate('/hasta-publica');
        },
      },
      {
        active: true,
        label: 'Criação da Declaração',
      },
    ];
    context?.setCrumbs(crumbs);
    setAnoDeclaracao(parseInt(sessionStorage.getItem('anoDeclaracao') || '-1'));
    setMesDeclaracao(parseInt(sessionStorage.getItem('mesDeclaracao') || '-1'));
  }, [navigate]);

  const enviarDeclaracao = async () => {
    const declaracao = context?.declaracaoHastaPublica;
    if (declaracao) {
      if (!declaracao?.arrematacoes || declaracao?.arrematacoes?.length === 0) {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'É necessário adicionar pelo menos uma arrematação à declaração',
        });
      } else {
        await postEnviarDeclaracao(declaracao)
          .then(async () => {
            window.scrollTo(0, 0);
            navigate(`/hasta-publica/view?id=${declaracaoId}&status=enviado`);
          })
          .catch((err) => {
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: 'Erro ao enviar declaração, tente novamente mais tarde.',
              ...(err && { complementoSentryId: err }),
            });
          });
      }
    } else {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Declaração não encontrada',
      });
    }
    setIsModalEnviarDeclaracaoOpen(false);
  };

  useEffect(() => {
    const declaracaoAntiga = context?.declaracaoHastaPublica || declaracaoHastaPublicaInicial;
    const declaracao: IDeclaracaoHastaPublica = {
      ...declaracaoAntiga,
      id: declaracaoId,
      statusDeclaracao: 0,
      tipoDeclarante: 2,
      tipoDeclaracao: 0,
      declarante: {
        nomeRazaoSocial: declaranteData?.nomeRazaoSocial || '',
        cpfCnpj: cnpj || '',
      },
      exercicioMes: parseInt(sessionStorage.getItem('mesDeclaracao') || '-1'),
      exercicioAno: parseInt(sessionStorage.getItem('anoDeclaracao') || '-1'),
    };

    if (declaranteData && !isRascunhoDataLoading) {
      if (rascunhoData) {
        context?.setDeclaracaoHastaPublica(rascunhoData);
        setIsRetificadora(rascunhoData.tipoDeclaracao === 1);

        setMesDeclaracao(rascunhoData.exercicioMes);
        sessionStorage.setItem('mesDeclaracao', rascunhoData.exercicioMes.toString());

        setAnoDeclaracao(rascunhoData.exercicioAno);
        sessionStorage.setItem('anoDeclaracao', rascunhoData.exercicioAno.toString());
      } else {
        context?.setDeclaracaoHastaPublica(declaracao);
        (rascunhoError?.status !== 404 || !mesDeclaracao || mesDeclaracao === -1) &&
          setStatusError(rascunhoError?.status?.toString() ?? null);
      }
    } else if ((declaranteData !== undefined && !declaranteData) || !!declaranteError) {
      setStatusError('declarante');
    }
  }, [declaracaoId, declaranteData, rascunhoData, isRascunhoDataLoading, rascunhoError, declaranteError]);

  const stepButton = (
    onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void,
    icon: string,
    text: string,
    isPrimary: boolean = false,
    isDisabled: boolean = false
  ) => {
    return (
      <BrButton secondary={!isPrimary} onClick={onClick} primary={isPrimary} disabled={isDisabled}>
        <span className={styles.button}>
          <i className={icon} /> {text}
        </span>
      </BrButton>
    );
  };

  const excluirRetificacao = async () => {
    await deleteDeclaracaoById(declaracaoId)
      .then(() => {
        setTimeout(() => {
          mostrarNotificacao({
            tipo: 'success',
            conteudo: 'Retificação excluída com sucesso',
          });
        }, 100);

        window.scrollTo(0, 0);
        navigate(`/hasta-publica`);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao excluir a retificação, tente novamente mais tarde. Erro: ' + err,
        });
      });
  };

  const errorTitle = () => {
    if (statusError === '403') {
      return 'Acesso negado.';
    } else if (statusError === 'declarante') {
      return 'Erro ao carregar declarante.';
    } else if (statusError === '400' || !mesDeclaracao || mesDeclaracao === -1) {
      return 'Declaração não encontrada.';
    } else return undefined;
  };

  const errorDescription = () => {
    if (statusError === '403') {
      return 'Você não tem permissão para acessar essa declaração. Por favor, entre em contato com o suporte pelo e-mail corfem@anm.gov.br caso ache que isto é um erro.';
    } else if (statusError === 'declarante') {
      return 'Ocorreu um erro ao carregar os dados do declarante. Por favor, tente novamente mais tarde.';
    } else if (statusError === '400' || !mesDeclaracao || mesDeclaracao === -1) {
      return 'A declaração que você está tentando editar não foi encontrada. Por favor, verifique se o link está correto e tente novamente. Caso queira começar uma nova declaração, acesse a página inicial clicando no botão abaixo.';
    } else return undefined;
  };

  const changeStep = () => {
    if (step === 0 && context?.declaracaoHastaPublica?.regimeTributacao?.id === -1) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Selecione um Regime de Tributação para continuar',
      });
    } else setStep(step + 1);
  };

  return (
    <>
      <CustomDivider step={step} mes={mesDeclaracao} ano={anoDeclaracao} />
      <TituloSection type='hasta-publica' isSteps />
      {statusError !== null ? (
        <ErrorMessage
          errorMessage={rascunhoError?.message ?? ''}
          errorTitle={errorTitle()}
          errorDescription={errorDescription()}
          error={rascunhoError}
          onClickFunction={
            statusError === '400' || !mesDeclaracao || mesDeclaracao === -1
              ? () => navigate('/hasta-publica')
              : undefined
          }
          buttonMessage='Voltar para a página inicial'
        />
      ) : (
        <>
          {' '}
          <Modal
            title='Enviar Declaração de Arrematação em Hasta Pública'
            useScrim
            modalOpened={isModalEnviarDeclaracaoOpen}
          >
            <Modal.Body>
              Deseja confirmar envio da declaracao em nome de <strong>{declaranteData?.nomeRazaoSocial}</strong>?
            </Modal.Body>
            <Modal.Footer justify-content='end'>
              <Button secondary small m={2} onClick={() => setIsModalEnviarDeclaracaoOpen(false)}>
                Cancelar
              </Button>
              <CustomConfirmer data-testid='botao-confirmar' asyncCall={() => enviarDeclaracao()}>
                Confirmar
              </CustomConfirmer>
            </Modal.Footer>
          </Modal>
          <Modal title='Deseja realmente excluir a retificação?' useScrim modalOpened={isModalExcluirRetificacaoOpen}>
            <Modal.Body>As alterações realizadas nesta declaração serão perdidas.</Modal.Body>
            <Modal.Footer justify-content='end'>
              <Button secondary small m={2} onClick={() => setIsModalExcluirRetificacaoOpen(false)}>
                Cancelar
              </Button>
              <Button primary small m={2} onClick={() => excluirRetificacao()}>
                Excluir
              </Button>
            </Modal.Footer>
          </Modal>
          {step === 0 && (
            <HastaPublicaIdBasica
              declaranteData={declaranteData}
              isDeclaranteDataLoading={isDeclaranteDataLoading || isRascunhoDataLoading}
            />
          )}
          {step === 1 && (
            <HastaPublicaProcessoMinerario
              isNovaArrematacao={isNovaArrematacao}
              setNovaArrematacao={setNovaArrematacao}
            />
          )}
          {step === 2 && <HastaPublicaResumo />}
          {isRetificadora && !isNovaArrematacao && (
            <div className={styles.containerDelete}>
              <BrButton icon='trash' secondary onClick={() => setIsModalExcluirRetificacaoOpen(true)}>
                Excluir Retificação
              </BrButton>
            </div>
          )}
          <div className={styles.containerStep}>
            {step !== 0 && !isNovaArrematacao
              ? stepButton(() => setStep(step - 1), `fas fa-arrow-left`, 'Voltar')
              : !isNovaArrematacao && stepButton(() => navigate('/hasta-publica'), `fas fa-times`, 'Cancelar')}
            {step < 2 && !isNovaArrematacao
              ? stepButton(
                  () => changeStep(),
                  `fas fa-arrow-right`,
                  'Avançar',
                  true,
                  (step === 1 &&
                    (!context?.declaracaoHastaPublica?.arrematacoes ||
                      context?.declaracaoHastaPublica?.arrematacoes?.length === 0)) ||
                    isDeclaranteDataLoading ||
                    isRascunhoDataLoading
                )
              : !isNovaArrematacao &&
                stepButton(() => setIsModalEnviarDeclaracaoOpen(true), `fas fa-paper-plane`, 'Enviar', true)}
          </div>
        </>
      )}
    </>
  );
}

export default HastaPublicaEdit;
